import { useCallback, useEffect, useMemo, useState } from "react"
import { unwrapResult } from "@reduxjs/toolkit"
import { useAppDispatch, useAppSelector } from "../../../app/hooks"
import { DataGrid, GridColDef } from "@mui/x-data-grid"
import { useLocalization } from "@fluent/react"
import { Box, Fab, Paper, TableContainer } from "@mui/material"
import { GoToButton } from "../../../app/components/GoToButton"
import AddIcon from '@mui/icons-material/Add'
import { Link, useLocation, useNavigate } from "react-router-dom"
import { CreateRiskAnalysisDialog } from "./dialogs/CreateRiskAnalysisDialog"
import { LoadingContainer } from "../../../app/LoadingContainer"
import { ErrorContainer } from "../../../app/ErrorContainer"
import { selectSettings } from "../../settings/settingsSlice"
import { enUS, plPL } from "@mui/x-data-grid/locales"
import { loadAllRiskAnalysisDocuments, RiskAnalysisHeader } from "./riskAnalysisApi"
import { QuickSearchToolbar } from "../../../app/QuickSearchToolbar"
import { DocumentFilterPanel } from "../utils/DocumentFilterPanel"
import { If } from "../../../app/If"
import { URSListFilters } from "../utils/types"
import { useLocalStorage } from "../../../useLocalStorage"
import { defURSFilters, filtersToQueryString } from "../utils/helper"
import { getStructureName } from "../../device/Structure"
import { DocumentStatusPill } from "../controls/DocumentStatusPill"
import { Package } from "../../permissions/usePackage"

export const RiskAnalysisList = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { locale } = useAppSelector(selectSettings);
    const [documents, setDocuments] = useState<RiskAnalysisHeader[]>([]);
    const { l10n } = useLocalization()
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);

    const location = useLocation();

    const [createDialogOpen, setCreateDialogOpen] = useState(false)

    const [filters, setFilters] = useLocalStorage<URSListFilters>("RiskAnalysisListFilters", defURSFilters)
    const f = useMemo(() => filtersToQueryString(filters), [filters])

    const isSupplierRiskAnalysis = location.pathname.includes('supplier-risk-analysis');

    useEffect(() => {
        setIsLoading(true)
        dispatch(loadAllRiskAnalysisDocuments({ filter: f }))
            .then(unwrapResult)
            .then(setDocuments)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))

    }, [])

    const handleGoToClick = (id) => {
        if (!isSupplierRiskAnalysis) {
            navigate(`/new-risk-analysis/edit/${id}`)
        }
        else {
            navigate(`/supplier-risk-analysis/edit/${id}`)
        }
    };


    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef[] =
        [
            {
                field: 'no',
                minWidth: 150,
                headerName: l10n.getString('document-number', null, '_Document number'),
            },
            {
                field: 'version',
                headerName: l10n.getString('document-version', null, '_Document version'),
                valueGetter: (_, row) => row.version,
            },
            {
                field: 'title',
                headerName: l10n.getString('document-title', null, '_Document title'),
                minWidth: 300,
                flex: 1,
            },
            {
                field: 'structureId',
                headerName: l10n.getString('structure', null, '_Structure'),
                width: 300,
                valueGetter: (_, row) => getStructureName(row.structureId),
            },
            {
                field: 'createdByFullName',
                headerName: l10n.getString('document-created-by', null, '_Author'),
                width: 200,
            },
            {
                field: 'createDate',
                type: 'date',
                width: 150,
                headerName: l10n.getString('meeting-minutes-create-date', null, 'Data utworzenia'),
                valueGetter: (_, row) => new Date(row.createDate),
            },

            {
                field: 'status',
                headerName: l10n.getString("document-status", null, "_Status"),
                width: 150,
                valueGetter: (_, row) => row.status,
                renderCell: (params) => (
                    <div>
                        <DocumentStatusPill status={params.row.status} size="small" maxWidth={160} />
                    </div>
                ),
            },
            {
                field: 'actions',
                type: 'actions',
                width: 60,
                align: 'left',
                headerAlign: 'left',
                renderCell: (params) => (
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-start' }}>
                        <GoToButton onClick={() => handleGoToClick(params.row.id)} />
                    </Box>
                )
            }
        ];

    const reload = useCallback(() => {
        setIsLoading(true)
        dispatch(loadAllRiskAnalysisDocuments({ filter: f }))
            .then(unwrapResult)
            .then(setDocuments)
            .catch((error) => { console.error(error); setIsError(true); setError(error); })
            .finally(() => setIsLoading(false))
    }, [f])


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return <>
        <DocumentFilterPanel filters={filters} onFilterChange={setFilters} reload={reload} />

        <If condition={!isLoading} otherwise={<LoadingContainer />}>
            <TableContainer component={Paper}>
                <DataGrid
                    style={{ minHeight: 100, height: 'calc(100vh - 250px)' }}
                    localeText={getCustomLocaleText()}
                    rows={documents}
                    columns={columns}
                    slots={{ toolbar: QuickSearchToolbar }}
                    onRowDoubleClick={({ row }) => handleGoToClick(row.id)}
                    initialState={{
                        sorting: {
                            sortModel: [{ field: 'createDate', sort: 'desc' }],
                        },
                    }}
                />
                <Box sx={{
                    display: 'flex',
                }}>
                    <Package name="RiskAnalysis" access="ReadWrite">
                        <Fab sx={{
                            margin: 1,
                        }} component={Link} to={`create`} color="secondary" size="small" >
                            <AddIcon />
                        </Fab>
                    </Package>
                </Box>
                <Box sx={{
                    display: 'flex',
                }}>
                    {/* <Fab sx={{
                        margin: 1,
                    }} onClick={() => setCreateDialogOpen(true)} color="secondary" size="small">
                        <AddIcon />
                    </Fab> */}
                </Box>
            </TableContainer>
        </If>

        <CreateRiskAnalysisDialog
            open={createDialogOpen}
            onClose={() => setCreateDialogOpen(false)}
        />
    </>
}
